// src/components/pages/Pessoa/modules/usePessoaEmpresaModule.js

import { useCallback, useEffect, useMemo, useState } from 'react';
import * as PessoaAPI from '../../../../api/pessoa';
import * as TabelaAPI from '../../../../api/tabela';

function createEmpresaDraft(pessoaId = null) {
    return {
        uuid: null,
        pessoaId: pessoaId || null,
        empresaId: null,
        Empresa: {},
        ativo: false,
        funcao: 'Adicionar',
    };
}

export function usePessoaEmpresaModule({ pessoaId, rows = [], setRows, setFlashMessage }) {
    const [draft, setDraft] = useState(createEmpresaDraft(pessoaId));
    const [editingIndex, setEditingIndex] = useState(null);

    useEffect(() => {
        setDraft((prev) => ({
            ...prev,
            pessoaId: pessoaId || null,
        }));
    }, [pessoaId]);

    const resetDraft = useCallback(() => {
        setDraft(createEmpresaDraft(pessoaId));
        setEditingIndex(null);
    }, [pessoaId]);

    const handleEdit = useCallback(
        (row, index) => {
            setDraft({
                index,
                uuid: row.uuid || null,
                pessoaId: row.pessoaId || pessoaId || null,
                empresaId: row.empresaId || null,
                Empresa: row.Empresa || {},
                ativo: !!row.ativo,
                funcao: 'Editar',
            });
            setEditingIndex(index);
        },
        [pessoaId]
    );

    const handleDelete = useCallback(
        async (row, index) => {
            const payload = {
                index,
                uuid: row.uuid || null,
                pessoaId: row.pessoaId || pessoaId || null,
                empresaId: row.empresaId || null,
                Empresa: row.Empresa || {},
                ativo: !!row.ativo,
                funcao: 'deletar',
            };

            if (!pessoaId) {
                setRows((prev) => prev.filter((_, i) => i !== index));
                setFlashMessage?.('Filial removida da lista.', 'success');
                if (editingIndex === index) resetDraft();
                return;
            }

            try {
                const result = await PessoaAPI.filial(payload);

                if (result?.data?.type === 'success') {
                    setRows(result.data.resultado || []);
                }

                setFlashMessage?.(result?.data?.message || 'Operação realizada.', result?.data?.type || 'success');

                if (editingIndex === index) resetDraft();
            } catch (error) {
                console.error(error);
                setFlashMessage?.(error?.response?.data?.message || 'Erro ao excluir filial.', 'error');
            }
        },
        [pessoaId, setRows, setFlashMessage, editingIndex, resetDraft]
    );

    const handleSave = useCallback(async () => {
        if (!draft.empresaId) {
            setFlashMessage?.('Selecione a empresa.', 'warning');
            return;
        }

        const payload = {
            ...draft,
            pessoaId: pessoaId || draft.pessoaId || null,
        };

        if (!pessoaId) {
            if (String(payload.funcao).toLowerCase() === 'editar' && editingIndex !== null) {
                let empresa = payload.Empresa;

                if (!empresa?.id && payload.empresaId) {
                    try {
                        empresa = await TabelaAPI.tabelaById(null, 'Empresa', payload.empresaId);
                    } catch (error) {
                        console.error(error);
                    }
                }

                setRows((prev) =>
                    prev.map((item, index) =>
                        index === editingIndex
                            ? {
                                  ...item,
                                  ...payload,
                                  Empresa: empresa || item.Empresa || {},
                              }
                            : item
                    )
                );
                setFlashMessage?.('Filial atualizada na lista.', 'success');
            } else {
                let empresa = payload.Empresa;

                if (!empresa?.id && payload.empresaId) {
                    try {
                        empresa = await TabelaAPI.tabelaById(null, 'Empresa', payload.empresaId);
                    } catch (error) {
                        console.error(error);
                    }
                }

                setRows((prev) => [
                    ...prev,
                    {
                        ...payload,
                        Empresa: empresa || {},
                        uuid: payload.uuid || `draft-filial-${Date.now()}`,
                    },
                ]);
                setFlashMessage?.('Filial adicionada na lista.', 'success');
            }

            resetDraft();
            return;
        }

        try {
            const result = await PessoaAPI.filial(payload);

            if (result?.data?.type === 'success') {
                setRows(result.data.resultado || []);
            }

            setFlashMessage?.(result?.data?.message || 'Operação realizada.', result?.data?.type || 'success');
            resetDraft();
        } catch (error) {
            console.error(error);
            setFlashMessage?.(error?.response?.data?.message || 'Erro ao salvar filial.', 'error');
        }
    }, [draft, pessoaId, editingIndex, resetDraft, setRows, setFlashMessage]);

    const columns = useMemo(
        () => [
            {
                id: 'filiais',
                header: 'Filiais permitidas',
                columns: [
                    {
                        accessorFn: (row) => row?.Empresa?.cnpj || '',
                        accessorKey: 'Empresa.cnpj',
                        header: 'CNPJ',
                        size: 150,
                    },
                    {
                        accessorFn: (row) => row?.Empresa?.razaoSocial || '',
                        accessorKey: 'Empresa.razaoSocial',
                        header: 'Razão Social',
                        size: 280,
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        size: 80,
                        Cell: ({ row }) => (row.original.ativo ? 'Sim' : 'Não'),
                    },
                ],
            },
        ],
        []
    );

    return {
        state: {
            items: rows || [],
            rowCount: rows?.length || 0,
            isLoading: false,
            isRefetching: false,
            isError: false,
            novaFilial: draft,
        },
        actions: {
            setNovaFilial: setDraft,
            limpar: resetDraft,
            submit: handleSave,
            handleEditarOuDeletar: (row, index, funcao) => {
                if (String(funcao).toLowerCase() === 'editar') {
                    handleEdit(row, index);
                    return;
                }
                handleDelete(row, index);
            },
            change: (e) => {
                const { name, value, checked } = e.target;

                if (name === 'novaFilial.empresaId') {
                    setDraft((prev) => ({
                        ...prev,
                        pessoaId: pessoaId || null,
                        empresaId: isNaN(value) ? null : Number(value),
                    }));
                    return;
                }

                if (name === 'novaFilial.ativo') {
                    setDraft((prev) => ({
                        ...prev,
                        pessoaId: pessoaId || null,
                        ativo: checked,
                    }));
                }
            },
            focus: () => {},
            blur: () => {},
        },
        columns,
    };
}
